<template>
    <IdeoFormGroup>
        <IdeoFormCheckbox v-model="form.settings.isExpertInsights">
            <span>{{ $t('[[[Expert insights]]]') }} - {{ $t('[[[eksportuj odnośniki do Lex (bloczek_openlex.xml)]]]') }}</span>
        </IdeoFormCheckbox>
    </IdeoFormGroup>

    <IdeoFormGroup :label="$t('[[[Odnośniki]]]')" :invalid-feedback="form.$errors.first('settings.references')" :state="form.$errors.state('settings.references')">
        <div v-for="(reference, i) in references" :key="reference.id" class="d-flex">
            <div class="input-group-prepend me-2">
                <span class="input-group-text">{{ i + 1 }}</span>
            </div>
            <div class="flex-fill me-2">
                <div class="input-group flex-fill">
                    <IdeoFormGroup class="flex-fill">
                        <IdeoFormInput v-model="reference.title" type="text" name="title" :placeholder="$t('[[[Tytuł odnośnika]]]')" class=" mb-1" />
                        <IdeoFormInput v-model="reference.link" type="text" name="link" :placeholder="$t('[[[Link odnośnika]]]')" />
                    </IdeoFormGroup>
                </div>
            </div>
            <div>
                <div class="input-group flex-fill me-2">
                    <ActionButton
                        variant="danger"
                        icon="far fa-times"
                        :title="$t('[[[Usuń odnośnik]]]')"
                        :confirm="$t('[[[Potwierdzenie usunięcia]]]')"
                        :disabled="references?.length < 2"
                        class=" me-2"
                        @click="removeReference(i)"
                    />
                    <ActionButton
                        variant="secondary"
                        icon="far fa-arrow-up"
                        :title="$t('[[[Przenieś wyżej]]]') "
                        :disabled="i == 0"
                        class=" me-2"
                        @click="referenceUp(i)"
                    />
                    <ActionButton variant="secondary" icon="far fa-arrow-down" :title="$t('[[[Przenieś niżej]]]')" :disabled="i == references?.length - 1" @click="referenceDown(i)" />
                </div>
            </div>
        </div>

        <div v-if="references?.length < 10" class="pt-1">
            <ActionButton variant="light" class="w-100" icon="far fa-plus" :text="$t('[[[Dodaj odnośnik]]]')" @click="addReference()" />
        </div>
    </IdeoFormGroup>
</template>

<script lang="ts" setup>
import { computedAsync } from '@vueuse/core';

interface FormSettings
{
    settings: {
        isExpertInsights: boolean;
        references: ReferenceModel[];
    };
}

interface ReferenceModel
{
    id: number;
    title: string;
    link: string;
}

defineOptions({
    name: 'WkReferenceListSettings'
});

const model = defineModel<FormType<FormSettings>>();
const defaults: FormSettings['settings'] = {
    isExpertInsights: false,
    references: []
};

const form = computed(() => model.value);

onMounted(async () =>
{
    form.value.settings = { ...defaults, ...form.value.settings };
});

const references = computedAsync(async () =>
{
    return form.value.settings.references || [];
});

const addReference = async (): Promise<void> =>
{
    const maxId = form.value.settings.references.length > 1 ? form.value.settings.references.reduce((a, b) => a.id > b.id ? a : b).id : 1;

    form.value.settings.references.push({
        id: maxId + 1,
        title: '',
        link: ''
    });
};

const removeReference = async (index: number): Promise<void> =>
{
    const items = form.value.settings.references.slice();

    items.splice(index, 1);
    form.value.settings.references = items;
};

const referenceUp = async (index: number): Promise<void> =>
{
    if (index > 0)
    {
        const references = form.value.settings.references.splice(index, 1);

        form.value.settings.references.splice(index - 1, 0, references[0]);
    }
};

const referenceDown = async (index: number): Promise<void> =>
{
    if (index < form.value.settings.references.length - 1)
    {
        const references = form.value.settings.references.splice(index, 1);

        form.value.settings.references.splice(index + 1, 0, references[0]);
    }
};

async function validate()
{
    form.value.validate(schema => ({
        settings: schema.object({
            references: schema.array(
                schema.object({
                    title: schema.string().required($t('[[[Tytuły są wymagane]]]')),
                    link: schema.string().required($t('[[[Linki są wymagane]]]'))
                })
            )
        })
    }));
}

defineExpose({
    validate
});
</script>
